/**
 * Confirms a "Still need help" request and collects triage details for Lion
 * support. The Insight (or alert) is attached by the backend; this only adds
 * what the customer knows that the telemetry doesn't.
 */
import * as React from "react";
import { Button } from "../../../../ui/components/Button";
import { Callout } from "../../../../ui/components/Callout";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { Modal } from "../../../../ui/components/Modal";
import { PhoneInput, validatePhone, getDigitCount } from "../../../../ui/components/Form";
import { useT } from "../../../lib/i18n";
import type { InsightSupportFields, InsightSupportImpact } from "../../../lib/api/insights";
import * as s from "./SupportRequestModal.css";

const NOTES_MAX = 2000;
const IMPACTS: InsightSupportImpact[] = ["down", "degraded", "working"];

export interface SupportRequestModalProps {
  open: boolean;
  /** Shown as what gets attached, e.g. the Insight title or alert name. */
  subject: string;
  alertCode?: string | null;
  /** The Insight's self-service steps, offered as "already tried" checkboxes. */
  steps?: string[];
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (fields: InsightSupportFields) => void;
}

export function SupportRequestModal({
  open,
  subject,
  alertCode,
  steps = [],
  loading = false,
  error = null,
  onCancel,
  onSubmit,
}: SupportRequestModalProps) {
  const t = useT();
  const [impact, setImpact] = React.useState<InsightSupportImpact | null>(null);
  const [tried, setTried] = React.useState<Set<number>>(new Set());
  const [notes, setNotes] = React.useState("");
  const [countryCode, setCountryCode] = React.useState("+1");
  const [phone, setPhone] = React.useState("");
  const [phoneTouched, setPhoneTouched] = React.useState(false);

  React.useEffect(() => {
    if (open) return;
    setImpact(null);
    setTried(new Set());
    setNotes("");
    setPhone("");
    setPhoneTouched(false);
  }, [open]);

  const handleClose = React.useCallback(() => {
    if (!loading) onCancel();
  }, [loading, onCancel]);

  const hasPhone = getDigitCount(phone) > 0;
  const phoneError = hasPhone ? validatePhone(countryCode, phone) : null;
  const canSubmit = impact !== null && !phoneError && !loading;

  const toggleStep = (index: number, checked: boolean) =>
    setTried((prev) => {
      const next = new Set(prev);
      if (checked) next.add(index);
      else next.delete(index);
      return next;
    });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !impact) return;
    onSubmit({
      impact,
      stepsTried: steps.filter((_, i) => tried.has(i)),
      notes: notes.trim() || null,
      callbackPhone: hasPhone ? `${countryCode} ${phone}`.trim() : null,
    });
  };

  return (
    <Modal
      open={open}
      setOpen={(next) => {
        if (!next) handleClose();
      }}
      closeOnClickOff={!loading}
      minWidth="420px"
      minHeight="auto"
      maxWidth="560px"
    >
      <Modal.Form onSubmit={submit} noValidate>
        <Modal.Header>
          <h3>{t("pro.products.insights.supportModal.title")}</h3>
        </Modal.Header>
        <Modal.Body>
          <div className={s.body}>
            <p className={s.text}>{t("pro.products.insights.supportModal.intro")}</p>

            <div className={s.attached}>
              <span className={s.attachedTitle}>
                {subject}
                {alertCode ? ` (${alertCode})` : ""}
              </span>
              {t("pro.products.insights.supportModal.attached")}
            </div>

            <fieldset className={s.fieldset} disabled={loading}>
              <legend className={s.legend}>{t("pro.products.insights.supportModal.impact")}</legend>
              {IMPACTS.map((value) => (
                <label key={value} className={s.option}>
                  <input
                    type="radio"
                    name="support-impact"
                    className={s.radio}
                    value={value}
                    checked={impact === value}
                    onChange={() => setImpact(value)}
                  />
                  {t(`pro.products.insights.supportModal.impact.${value}`)}
                </label>
              ))}
            </fieldset>

            {steps.length > 0 ? (
              <fieldset className={s.fieldset} disabled={loading}>
                <legend className={s.legend}>
                  {t("pro.products.insights.supportModal.tried")}{" "}
                  <span className={s.optional}>{t("pro.products.insights.supportModal.optional")}</span>
                </legend>
                {steps.map((step, i) => (
                  <label key={i} className={s.option}>
                    <Checkbox checked={tried.has(i)} onCheckedChange={(c) => toggleStep(i, c)} aria-label={step} />
                    {step}
                  </label>
                ))}
              </fieldset>
            ) : null}

            <div>
              <label className={s.label} htmlFor="support-request-notes">
                {t("pro.products.insights.supportModal.notes")}{" "}
                <span className={s.optional}>{t("pro.products.insights.supportModal.optional")}</span>
              </label>
              <textarea
                id="support-request-notes"
                className={s.textarea}
                value={notes}
                maxLength={NOTES_MAX}
                onChange={(e) => setNotes(e.target.value)}
                disabled={loading}
                placeholder={t("pro.products.insights.supportModal.notesPlaceholder")}
              />
              {notes.length > NOTES_MAX * 0.8 ? (
                <div className={s.counter}>
                  {notes.length}/{NOTES_MAX}
                </div>
              ) : null}
            </div>

            <div onBlur={() => setPhoneTouched(true)}>
              <PhoneInput
                id="support-request-phone"
                label={`${t("pro.products.insights.supportModal.phone")} ${t("pro.products.insights.supportModal.optional")}`}
                countryCode={countryCode}
                phone={phone}
                onCountryCodeChange={setCountryCode}
                onPhoneChange={setPhone}
                disabled={loading}
                hint={t("pro.products.insights.supportModal.phoneHint")}
                error={phoneTouched && phoneError ? phoneError : undefined}
              />
            </div>

            {error ? <Callout variant="error">{error}</Callout> : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" color="neutral" variant="outline" size="md" disabled={loading} onClick={handleClose}>
            {t("pro.products.insights.supportModal.cancel")}
          </Button>
          <Button type="submit" color="primary" variant="solid" size="md" loading={loading} disabled={!canSubmit}>
            {t("pro.products.insights.supportModal.submit")}
          </Button>
        </Modal.Footer>
      </Modal.Form>
    </Modal>
  );
}
